import { CommonModule } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { RouterModule } from '@angular/router';
import { EnrollmentStatus } from '@lb/api/mylb/enums/enrollment-status.enum';
import { UserIconComponent } from '@lb/components';
import { format } from 'date-fns';
import { SchedulerProgressComponent } from '../scheduler-progress/scheduler-progress.component';
import { MDContentBoxComponent, MDHeaderComponent, MDIconComponent } from '@melodic-ui/components';
import { ProgramCompletionSummaryComponent } from '@lb/components/program-completion-summary/program-completion-summary.component';
import { NoInstructorComponent } from '@lb/components/no-instructor/no-instructor.component';
import { EnrollmentTagComponent } from '@lb/components/enrollment-tag/enrollment-tag.component';
import { IEnrollmentDetailItem } from '@lb/api/mylb/interfaces';

@Component({
	standalone: true,
	imports: [
		MDContentBoxComponent,
		UserIconComponent,
		MDIconComponent,
		ProgramCompletionSummaryComponent,
		MDHeaderComponent,
		CommonModule,
		SchedulerProgressComponent,
		RouterModule,
		NoInstructorComponent,
		EnrollmentTagComponent
	],
	selector: 'lrnr-enrollment-info-panel',
	templateUrl: 'enrollment-info-panel.component.html',
	styleUrl: './enrollment-info-panel.component.scss'
})
export class EnrollmentInfoPanelComponent {
	/**
	 * Sourced from Enrollment.Api (D19-inclusive) rather than the legacy monolith summary. The detail
	 * item's startDate/endDate already coalesce server-side to "first/last actual lesson, else selected
	 * start / anticipated end", which is exactly the branching this panel used to do client-side from
	 * lesson state.
	 */
	public currentEnrollment = input.required<IEnrollmentDetailItem>();

	languageLevel = computed(() => this.currentEnrollment().languageLevels);
	languageName = computed(() => this.currentEnrollment().languageName);
	programName = computed(() => this.currentEnrollment().programName);
	lessonCount = computed(() => this.currentEnrollment().lessonCount);

	isAwaitingSchedule = computed(() => this.currentEnrollment().enrollmentStatusID === EnrollmentStatus.AwaitingSchedule);
	isAwaitingMatch = computed(() => this.currentEnrollment().enrollmentStatusID === EnrollmentStatus.AwaitingMatch);

	startDateLabel = computed(() => {
		if (this.isAwaitingSchedule() || this.isAwaitingMatch()) {
			return 'Selected Date';
		}

		return 'Date of the First Lesson';
	});

	startDate = computed(() => {
		const dateVal = this.currentEnrollment().startDate;
		const date = dateVal ? new Date(dateVal) : new Date();
		return format(date, 'd MMM yyyy');
	});

	endDateLabel = computed(() => {
		if (this.isAwaitingSchedule() || this.isAwaitingMatch()) {
			return 'Anticipated End Date';
		}

		return 'Date of Last Lesson';
	});

	endDate = computed(() => {
		const dateVal = this.currentEnrollment().endDate;
		const date = dateVal ? new Date(dateVal) : new Date();
		return format(date, 'd MMM yyyy');
	});
}
